import { create } from 'zustand';

export type InstanceStoreFactory<TStore, TInitial> = (
  instanceId: string,
  initial: TInitial,
) => TStore;

export type InstanceManager<TStore, TInitial> = {
  /**
   * Base (main page) store. Defined as a getter so callers always read the
   * current ref after setBaseStore — never a stale snapshot. Spreading the
   * manager via `{...manager}` collapses this getter to a value, so wrappers
   * (see createDependencyGraphInstanceManager) must forward fields explicitly
   * or re-define the getter.
   */
  readonly baseStore: TStore;
  /**
   * Replace the base store in place. The manager identity (and the instances
   * Map) is preserved — callers that have already registered instances keep
   * them. Used by providers whose base derives from upstream state that can
   * change without remounting (e.g. PageDependencyGraphManagerProvider's
   * baseContext re-derives when the active interfacePage object identity
   * changes; without setBaseStore the only option was to recreate the manager
   * which silently dropped every registered instance).
   */
  setBaseStore: (next: TStore) => void;
  createInstance: (instanceId: string, initial: TInitial) => void;
  removeInstance: (instanceId: string) => void;
  getStore: (instanceId?: string) => TStore;
  getInstanceStore: (instanceId: string) => TStore | undefined;
  useInstanceIds: () => string[];
  /**
   * Per-id reactive membership check. Subscribes via a boolean selector so the
   * caller only re-renders when this specific instance enters or leaves the
   * manager — avoids re-rendering every consumer on every unrelated add/remove
   * (which is what `useInstanceIds()` does, since it returns the full array).
   */
  useHasInstance: (instanceId: string | undefined) => boolean;
};

/**
 * Generic factory for per-instance store managers. Owns a base (main page) store and a map of
 * instance entries keyed by instanceId. getStore() with no id returns the base; with an id returns
 * the instance store.
 *
 * Lifetime semantics: createInstance/removeInstance form an acquire/release pair. Each entry
 * holds a refcount; createInstance increments (or creates on 0→1), removeInstance decrements
 * (and schedules disposal on 1→0 via setTimeout(0)). A subsequent createInstance before the
 * timer fires cancels it and reuses the existing store. This is what survives React 18
 * StrictMode's synthetic mount→unmount→remount: the synchronous synthetic unmount schedules
 * disposal, the synthetic remount on the next tick's effect cancels it, store + state preserved.
 * Real unmount has no follow-up acquire, timer fires, entry is dropped.
 *
 * notFoundMessage is a per-manager label so thrown errors name the right store type.
 */
type InstanceEntry<TStore> = {
  store: TStore;
  refs: number;
  disposeTimer: ReturnType<typeof setTimeout> | null;
};

const TIMEOUT_DELAY = 200;

/**
 * Optional lifecycle hooks fired on the REAL 0→1 acquire and 1→0 dispose transitions
 * (not on every refcount change, and not on StrictMode synthetic churn that gets revived
 * before the dispose timer fires). Used by GlobalStateStoreManager to register/unregister
 * Pulse scopes. The other managers pass none and are unaffected.
 */
export type InstanceManagerHooks<TStore> = {
  onAcquire?: (instanceId: string, store: TStore) => void;
  onDispose?: (instanceId: string) => void;
};

export function createInstanceManager<TStore, TInitial>(
  initialBaseStore: TStore,
  createInstanceStore: InstanceStoreFactory<TStore, TInitial>,
  notFoundMessage: (instanceId: string) => string,
  hooks?: InstanceManagerHooks<TStore>,
): InstanceManager<TStore, TInitial> {
  const instances = new Map<string, InstanceEntry<TStore>>();
  const idsStore = create<{ ids: string[] }>(() => ({ ids: [] as string[] }));
  // Mutable closure ref so providers can swap the base via setBaseStore
  // without forcing a fresh manager (and losing the instances Map). All reads
  // of the base — both getStore(undefined) and the baseStore getter on the
  // returned object — go through this ref so they always see the latest.
  let currentBaseStore = initialBaseStore;

  const setBaseStore = (next: TStore): void => {
    currentBaseStore = next;
  };

  const createInstance = (instanceId: string, initial: TInitial): void => {
    const entry = instances.get(instanceId);
    if (entry) {
      // Cancel any pending dispose — a new acquire arrived in time to revive
      // the entry (typical StrictMode synthetic remount path).
      if (entry.disposeTimer) {
        clearTimeout(entry.disposeTimer);
        entry.disposeTimer = null;
      }
      entry.refs++;
      return;
    }
    const store = createInstanceStore(instanceId, initial);
    instances.set(instanceId, { store, refs: 1, disposeTimer: null });
    idsStore.setState((prev) => ({ ids: [...prev.ids, instanceId] }));
    hooks?.onAcquire?.(instanceId, store);
  };

  const removeInstance = (instanceId: string): void => {
    const entry = instances.get(instanceId);
    if (!entry) return;
    entry.refs--;
    if (entry.refs > 0) return;
    // refs hit 0 — defer disposal so a same-tick reacquire (StrictMode synthetic
    // remount, fast unmount/remount) can revive the entry without losing state.
    entry.disposeTimer = setTimeout(() => {
      instances.delete(instanceId);
      idsStore.setState((prev) => ({ ids: prev.ids.filter((id) => id !== instanceId) }));
      hooks?.onDispose?.(instanceId);
    }, TIMEOUT_DELAY);
  };

  const getStore = (instanceId?: string): TStore => {
    if (instanceId === undefined) return currentBaseStore;
    const entry = instances.get(instanceId);
    if (!entry) {
      throw new Error(notFoundMessage(instanceId));
    }
    return entry.store;
  };

  const getInstanceStore = (instanceId: string): TStore | undefined => {
    return instances.get(instanceId)?.store;
  };

  /** reactive hook: subscribe to the list of active instance instanceIds */
  const useInstanceIds = (): string[] => {
    return idsStore((state) => state.ids);
  };

  const useHasInstance = (instanceId: string | undefined): boolean => {
    return idsStore((state) => (instanceId === undefined ? false : state.ids.includes(instanceId)));
  };

  return {
    get baseStore() {
      return currentBaseStore;
    },
    setBaseStore,
    createInstance,
    removeInstance,
    getStore,
    getInstanceStore,
    useInstanceIds,
    useHasInstance,
  };
}
